import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Appointments from './pages/Appointments';
import Notifications from './pages/Notifications';
import Documents from './pages/Documents';
import CrashReport from './pages/CrashReport';
import Messages from './pages/Messages';
import Chat from './pages/Chat';
import AuthPage from './pages/AuthPage';
import Profile from './pages/Profile';
import UserManagement from './pages/UserManagement';
import ArchiveCars from './pages/ArchiveCars';
import Complaints from './pages/Complaints';
import RouteSheet from './pages/RouteSheet';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SetPasswordPage from './pages/SetPasswordPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from './components/Footer';
import BugReport from './pages/BugReport';
import { ChatProvider } from './context/ChatContext';
import pushNotificationService from './services/PushNotificationService';
import { authAPI, usersAPI } from './api/api';
import { applyBrandAssets, getBrandDisplayName } from './utils/brandHelper';

function PrivateRoute({ children }) {
  const isLoggedIn = !!localStorage.getItem('loggedInUser');
  return isLoggedIn ? children : <Navigate to="/auth" replace />;
}

function AppContent({ isSidebarClosed, setIsSidebarClosed }) {
  const location = useLocation();
  const showFooter = location.pathname !== '/auth';

  useEffect(() => {
    document.title = `myCar - ${getBrandDisplayName()}`;
    applyBrandAssets();

    const refreshUserData = () => {
      if (authAPI.isAuthenticated()) {
        authAPI.refreshUser().then((userData) => {
          if (userData) {
            document.title = `myCar - ${getBrandDisplayName()}`;
          }
        });
      }
    };

    refreshUserData();

    const currentUser = authAPI.getCurrentUser();
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'boss';
    const intervalMs = isAdmin ? 5 * 60 * 1000 : 5 * 1000;
    const refreshInterval = setInterval(refreshUserData, intervalMs);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUserData();
      }
    };

    window.addEventListener('focus', refreshUserData);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshUserData);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    // Initialize push notifications when user is logged in
    const initPushNotifications = async () => {
      const user = authAPI.getCurrentUser();
      if (!user || location.pathname === '/auth') {
        return;
      }

      // Check if already asked or subscribed
      const hasAskedPermission = localStorage.getItem('push_permission_asked');
      const isSubscribed = pushNotificationService.isSubscribed();

      if (isSubscribed || hasAskedPermission === 'true') {
        return;
      }

      // Wait a bit before showing the prompt (better UX)
      setTimeout(async () => {
        try {
          await pushNotificationService.init();
          const permission = await pushNotificationService.getPermissionStatus();

          

          // Only show prompt if permission is default (not granted or denied)
          if (permission === 'default') {
           

            // Request permission and subscribe if granted
            try {
              const result = await pushNotificationService.subscribe();
              

              // Mark that we asked (after the attempt)
              localStorage.setItem('push_permission_asked', 'true');
            } catch (subscribeError) {
              console.error('[App] Subscribe error:', subscribeError);
              // Mark as asked even on error to avoid repeated prompts
              localStorage.setItem('push_permission_asked', 'true');
            }
          } 
        } catch (error) {
          console.error('[App] Push notification initialization error:', error);
        }
      }, 2000); // Wait 2 seconds after page load
    };

    initPushNotifications();
  }, [location.pathname]);

  return (
    <>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/" element={<PrivateRoute><Home isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/appointments" element={<PrivateRoute><Appointments isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/notifications" element={<PrivateRoute><Notifications isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/documents" element={<PrivateRoute><Documents isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/crash-report" element={<PrivateRoute><CrashReport isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/messages" element={<PrivateRoute><Messages isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/chat" element={<PrivateRoute><Chat isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><Profile isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/user-management" element={<PrivateRoute><UserManagement isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/archive" element={<PrivateRoute><ArchiveCars isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/bug-report" element={<PrivateRoute><BugReport isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/complaints" element={<PrivateRoute><Complaints isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="/route" element={<PrivateRoute><RouteSheet isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
        <Route path="*" element={<PrivateRoute><Home isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} /></PrivateRoute>} />
      </Routes>
      {showFooter && <Footer isSidebarClosed={isSidebarClosed} />}
      <ToastContainer position="top-center" autoClose={1500} hideProgressBar theme="colored" />
    </>
  );
}

function App() {
  const [isSidebarClosed, setIsSidebarClosed] = useState(window.innerWidth <= 600);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/firebase-messaging-sw.js').then(reg => {
        // Check for updates every 30 minutes
        setInterval(() => reg.update(), 30 * 60 * 1000);

        reg.onupdatefound = () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          newWorker.onstatechange = () => {
            if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
              window.location.reload();
            }
          };
        };
      });
    }
  }, []);

  return (
    <Router>
      <ChatProvider>
        <AppContent isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
      </ChatProvider>
    </Router>
  );
}

export default App;
